<template lang="pug">
  #program-customization-navbar
    ProgramSettingsFormWrapper#general-settings(
      :form-data="formData"
      :disable-save.sync="disableSave"
      title=""
      disableReset
      :formControlIsText="false"
      :keys-to-reset="[]"
      :saveButtonText="saveButtonText"
    )
      ContentLoader(:width="300" :height="120" v-if="!programLoaded")
        rect(x="0", y="0", rx="4", ry="4", width="45%", height="100")
        rect(x="50%", y="0", rx="4", ry="4", width="50%", height="30")
        rect(x="50%", y="45", rx="4", ry="4", width="50%", height="30")
        rect(x="50%", y="90", rx="4", ry="4", width="50%", height="30")

      .columns(v-else)
        .column.is-12
          WtRadioQuestionInput.block(
            label="Show a Custom Nav Bar Button:"
            horizontal
          )
            template(v-slot:radioOne)
              BRadio.define-radio(
                v-model="customNavbarButton"
                :native-value="true"
              )
                | Yes
            template(v-slot:radioTwo)
              BRadio.define-radio(
                v-model="customNavbarButton"
                :native-value="false"
              )
                | No
          .columns
            .column.is-narrow.is-flex
              WtRadioQuestionInput.block(
                label="Link To:"
                horizontal
              )
                template(v-slot:radioOne)
                  BRadio.define-radio(
                    v-model="customButtonDestination"
                    native-value="externalLink"
                    :disabled="!customNavbarButton"
                  )
                    | External Link
                template(v-slot:radioTwo)
                  BRadio.define-radio(
                    v-model="customButtonDestination"
                    native-value="customPage"
                    :disabled="!customNavbarButton"
                  )
                    | Custom Page
            .column.is-narrow(v-if="customButtonDestination === 'externalLink'")
              BField(:message="urlError" :type="urlError ? 'is-danger' : null")
                BInput#custom-page-link(
                  type="text"
                  name="customPageLink"
                  v-model="customPageLink"
                  placeholder="Type URL here..."
                  :disabled="!customNavbarButton"
                  @input="onUrlChange($event)"
                )
          .columns
            .column.is-narrow
              BField
                BInput#custom-page-button-text(
                  type="text"
                  name="customButtonText"
                  v-model="customButtonText"
                  placeholder="Type button text here..."
                  :disabled="!customNavbarButton"
                )
          BField(label="Custom Page Content" v-if="customButtonDestination==='customPage'")
            WtWysiwyg(:content.sync="customPageContent")
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ContentLoader } from "vue-content-loader";
import programSettingsFormMixin from "@/mixins/programSettingsFormMixin";
import ProgramSettingsFormWrapper from "@@/ProgramSettingsAdmin/ProgramSettingsFormWrapper";
import ProgramSettingsFormControls from "@@/ProgramSettingsAdmin/ProgramSettingsFormControls";
import WtRadioQuestionInput from "@@/WtRadioQuestionInput/WtRadioQuestionInput";
import WtWysiwyg from "@@/WtWysiwyg/WtWysiwyg";

export default {
  name: "ProgramCustomizationNavbar",

  components: {
    ProgramSettingsFormWrapper,
    ContentLoader,
    ProgramSettingsFormControls,
    WtRadioQuestionInput,
    WtWysiwyg
  },

  mixins: [programSettingsFormMixin],

  data() {
    return {
      urlError: "",
      uploadInProgress: false
    };
  },

  computed: {
    ...mapGetters("program", [
      "coreProgramMeta",
      "programSettingsCopy",
      "programLoaded"
    ]),

    formData() {
      const data = {};

      if (
        !this.isOriginalValue("customNavbarButton", this.customNavbarButton)
      ) {
        data.customNavbarButton = this.customNavbarButton;
      }

      if (
        !this.isOriginalValue(
          "customButtonDestination",
          this.customButtonDestination
        )
      ) {
        data.customButtonDestination = this.customButtonDestination;
      }

      if (!this.isOriginalValue("customPageLink", this.customPageLink)) {
        data.customPageLink = this.customPageLink;
      }

      if (!this.isOriginalValue("customPageContent", this.customPageContent)) {
        data.customPageContent = this.customPageContent;
      }

      if (this.customNavbarButton) {
        data.customButtonStyle = "button";
      }

      if (!this.isOriginalValue("customButtonText", this.customButtonText)) {
        data.customButtonText = this.customButtonText;
      }

      return data || {};
    },

    customNavbarButton: {
      set(val) {
        this.updateProgramSettingsCopy({
          key: "customNavbarButton",
          value: val
        });
        this.validateForm("customNavbarButton", val);
      },
      get() {
        return this.programSettingsCopy.customNavbarButton;
      }
    },

    customButtonDestination: {
      set(val) {
        this.updateProgramSettingsCopy({
          key: "customButtonDestination",
          value: val
        });
        this.validateForm("customButtonDestination", val);
      },
      get() {
        return this.programSettingsCopy.customButtonDestination;
      }
    },

    customPageLink: {
      set(val) {
        this.updateProgramSettingsCopy({
          key: "customPageLink",
          value: val
        });
        this.validateForm("customPageLink", val);
      },
      get() {
        return this.programSettingsCopy.customPageLink;
      }
    },

    customButtonStyle: {
      set(val) {
        this.updateProgramSettingsCopy({
          key: "customButtonStyle",
          value: val
        });
        this.validateForm("customButtonStyle", val);
      },
      get() {
        return this.programSettingsCopy.customButtonStyle;
      }
    },

    customButtonText: {
      set(val) {
        this.updateProgramSettingsCopy({
          key: "customButtonText",
          value: val
        });
        this.validateForm("customButtonText", val);
      },
      get() {
        return this.programSettingsCopy.customButtonText;
      }
    },

    customPageContent: {
      set(val) {
        this.updateProgramSettingsCopy({
          key: "customPageContent",
          value: val
        });
        this.validateForm("customPageContent", val);
      },
      get() {
        return this.programSettingsCopy.customPageContent;
      }
    },

    saveButtonText() {
      if (this.coreProgramMeta.customButtonDestination) {
        return "Update";
      }
      return "Save";
    }
  },

  mounted() {
    this.onUrlChange = this._.debounce(newUrl => {
      if (!this.isValidHttpUrl(newUrl)) {
        this.urlError = "Please enter a valid URL";
        this.disableSave = true;
      } else {
        this.urlError = "";
      }
    }, 500);
  },

  methods: {
    ...mapActions("program", [
      "updateCoreMeta",
      "updateProgramSettingsCopy",
      "programSettingsUpdate"
    ]),

    ...mapActions(["snackBar"]),

    onUrlChange() {
      // defining this here, but is overwritten in the
      // mounted call.  Using debounce requires this
      // approach in order for it to work when
      // the component instance is available.
    },

    validateForm(field, val) {
      this.disableSave = this.isOriginalValue(field, val);
      // check if required field are completed
      const requiredValues = this.customButtonDestination;
      if (requiredValues) {
        const additionalRequiredValues = {
          externalLink: this.customPageLink,
          customPage: this.customPageContent,
          button: this.customButtonText
        };
        const additionalRequiredFieldsCompleted = !!additionalRequiredValues[
          this.customButtonDestination
        ];
        if (!additionalRequiredFieldsCompleted) {
          this.disableSave = true;
        }
      } else {
        this.disableSave = true;
      }
    },

    isValidHttpUrl(string) {
      let url;
      try {
        url = new URL(string);
      } catch (_) {
        return false;
      }
      return url.protocol === "http:" || url.protocol === "https:";
    }
  }
};
</script>

<style lang="sass">
#program-customization-navbar
  #custom-page-link, #custom-page-button-text
    width: 300px
    max-width: 100%
</style>
